import React, { useState, useRef, useEffect } from 'react';
import { FaSearch, FaCheck, FaTimes, FaWrench, FaFilter, FaBox, FaInfoCircle, FaSpinner } from 'react-icons/fa';

const SerialNumberModal = ({ isOpen, onClose, stockList, selectedStocks, setIsExchange, onCloseComplete, onSave }) => {
   const [searchQuery, setSearchQuery] = useState('');
   const searchRef = useRef(null);
   const [tempData, setTempData] = useState([]);
   const [filterInRepair, setFilterInRepair] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const isProcessingRef = useRef(false);

   useEffect(() => {
      if (isOpen && selectedStocks.length > 0) {
         setTempData(
            selectedStocks.map((item) => ({
               itemName: item?.itemName,
               serialNo: item?.serialNo,
               serialNo2: item?.serialNo2,
               isSend: false,
               isInRepair: item?.isInRepair || false,
            })),
         );
      }
      // Reset saving state when modal opens
      setIsSaving(false);
      isProcessingRef.current = false;
   }, [isOpen]);

   useEffect(() => {
      if (isOpen && searchRef.current) {
         searchRef.current.focus();
      }
   }, [isOpen]);

   const handleSelectStock = (e, stock) => {
      if (isSaving) return;

      if (e.target.checked) {
         setTempData((prevData) =>
            prevData.map((item) => (item.serialNo === stock.serialNo ? { ...item, isSend: true } : item)),
         );
      } else {
         setTempData((prevData) =>
            prevData.map((item) => (item.serialNo === stock.serialNo ? { ...item, isSend: false } : item)),
         );
      }
   };

   const handleSelectAll = (e) => {
      if (isSaving) return;

      const filteredData = getFilteredData();
      const allSelectable = filteredData.filter((item) => !item.isInRepair);
      const allCurrentlySelected = allSelectable.every((item) => item.isSend);

      setTempData((prevData) =>
         prevData.map((item) => {
            const isInFiltered = filteredData.some((filteredItem) => filteredItem.serialNo === item.serialNo);
            if (isInFiltered && !item.isInRepair) {
               return { ...item, isSend: !allCurrentlySelected };
            }
            return item;
         }),
      );
   };

   const handleSave = async () => {
      // Prevent multiple clicks
      if (isSaving || isProcessingRef.current) {
         console.log('Already saving, please wait...');
         return;
      }

      const data = tempData.filter((item) => item.isSend && !item.isInRepair);

      if (data.length === 0) return;

      // Set loading state - THIS WILL SHOW LOADER
      setIsSaving(true);
      isProcessingRef.current = true;

      console.log('Saving started...');

      try {
         // Call the onSave function
         await onSave(data);

         // Update exchange data
         setIsExchange(data);

         console.log('Saving completed successfully');
      } catch (error) {
         console.error('Error in saving:', error);
      } finally {
         // Reset loading state after a small delay to ensure loader is visible
         setTimeout(() => {
            setIsSaving(false);
            isProcessingRef.current = false;
            console.log('Loading state reset');
         }, 500);
      }
   };

   const getFilteredData = () => {
      let filtered = tempData;

      if (searchQuery) {
         filtered = filtered.filter(
            (item) =>
               item.itemName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
               item.serialNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
               item.serialNo2?.toLowerCase().includes(searchQuery.toLowerCase()),
         );
      }

      if (filterInRepair) {
         filtered = filtered.filter((item) => item.isInRepair);
      }

      return filtered;
   };

   const filteredData = getFilteredData();
   const selectedCount = tempData.filter((item) => item.isSend && !item.isInRepair).length;
   const totalSelectable = tempData.filter((item) => !item.isInRepair).length;

   if (!isOpen) return null;

   return (
      <div className="fixed inset-0 z-[999] overflow-y-auto">
         <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
               className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
               onClick={isSaving ? undefined : onClose}></div>

            <div className="relative inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
               {/* Header */}
               <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700">
                  <div className="flex items-center justify-between">
                     <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                           <FaBox className="w-6 h-6 text-white" />
                        </div>
                        <div>
                           <h3 className="text-xl font-semibold text-white">Manage Serial Numbers</h3>
                           <p className="text-blue-100 text-sm mt-1">Select items to include in CRM workflow</p>
                        </div>
                     </div>
                     <button
                        onClick={isSaving ? undefined : onClose}
                        disabled={isSaving}
                        className={`p-2 text-white rounded-lg transition-colors duration-200 ${
                           isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white hover:bg-opacity-20'
                        }`}>
                        <FaTimes className="w-5 h-5" />
                     </button>
                  </div>
               </div>

               {/* Body */}
               <div className="px-6 py-4 bg-gray-50">
                  {/* Search and Filters */}
                  <div className="flex flex-col sm:flex-row gap-4 mb-6">
                     <div className="flex-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                           <FaSearch className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                           ref={searchRef}
                           type="text"
                           className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                           placeholder="Search by item name or serial number..."
                           value={searchQuery}
                           onChange={(e) => setSearchQuery(e.target.value)}
                           disabled={isSaving}
                        />
                     </div>

                     <div className="flex items-center space-x-4">
                        <label
                           className={`flex items-center space-x-2 ${
                              isSaving ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                           }`}>
                           <div className="relative">
                              <input
                                 type="checkbox"
                                 className="sr-only"
                                 checked={filterInRepair}
                                 onChange={isSaving ? undefined : (e) => setFilterInRepair(e.target.checked)}
                                 disabled={isSaving}
                              />
                              <div
                                 className={`block w-12 h-6 rounded-full transition-colors duration-200 ${
                                    filterInRepair ? 'bg-blue-600' : 'bg-gray-300'
                                 }`}></div>
                              <div
                                 className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 ${
                                    filterInRepair ? 'transform translate-x-6' : ''
                                 }`}></div>
                           </div>
                           <span className="text-sm font-medium text-gray-700 flex items-center">
                              <FaFilter className="w-4 h-4 mr-1" />
                              Show only items in repair
                           </span>
                        </label>
                     </div>
                  </div>

                  {/* Selection Info */}
                  <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                     <div className="flex items-center space-x-4">
                        <FaInfoCircle className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                           Selected: {selectedCount} of {totalSelectable} available items
                        </span>
                        {selectedCount > 0 && (
                           <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                              {selectedCount} selected
                           </span>
                        )}
                     </div>
                     {totalSelectable > 0 && (
                        <button
                           onClick={isSaving ? undefined : handleSelectAll}
                           disabled={isSaving}
                           className={`text-sm font-medium transition-colors duration-200 flex items-center ${
                              isSaving ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'
                           }`}>
                           <FaCheck className="w-3 h-3 mr-1" />
                           Select all available
                        </button>
                     )}
                  </div>

                  {/* Table */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                     <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                           <thead className="bg-gray-50">
                              <tr>
                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Select
                                 </th>
                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Item Name
                                 </th>
                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Serial No 1
                                 </th>
                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Serial No 2
                                 </th>
                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                 </th>
                              </tr>
                           </thead>
                           <tbody className="bg-white divide-y divide-gray-200">
                              {filteredData.length > 0 ? (
                                 filteredData.map((item, index) => (
                                    <tr
                                       key={index}
                                       className={`transition-colors duration-150 ${
                                          item.isInRepair ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gray-50'
                                       } ${item.isSend ? 'bg-blue-50' : ''}`}>
                                       <td className="px-6 py-4 whitespace-nowrap">
                                          <input
                                             type="checkbox"
                                             checked={item?.isSend}
                                             disabled={item?.isInRepair || isSaving}
                                             onChange={isSaving ? undefined : (e) => handleSelectStock(e, item)}
                                             className={`w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                                                item.isInRepair || isSaving
                                                   ? 'opacity-50 cursor-not-allowed'
                                                   : 'cursor-pointer'
                                             }`}
                                          />
                                       </td>
                                       <td className="px-6 py-4 whitespace-nowrap">
                                          <div className="flex items-center">
                                             <span
                                                className={`text-sm font-medium ${
                                                   item.isInRepair ? 'text-gray-500' : 'text-gray-900'
                                                }`}>
                                                {item?.itemName}
                                             </span>
                                          </div>
                                       </td>
                                       <td className="px-6 py-4 whitespace-nowrap">
                                          <span
                                             className={`text-sm ${
                                                item.isInRepair ? 'text-gray-500' : 'text-gray-900'
                                             }`}>
                                             {item?.serialNo}
                                          </span>
                                       </td>
                                       <td className="px-6 py-4 whitespace-nowrap">
                                          <span
                                             className={`text-sm ${
                                                item.isInRepair ? 'text-gray-500' : 'text-gray-900'
                                             }`}>
                                             {item?.serialNo2}
                                          </span>
                                       </td>
                                       <td className="px-6 py-4 whitespace-nowrap">
                                          {item.isInRepair ? (
                                             <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                <FaWrench className="w-3 h-3 mr-1" />
                                                In Repair
                                             </span>
                                          ) : (
                                             <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                <FaCheck className="w-3 h-3 mr-1" />
                                                Available
                                             </span>
                                          )}
                                       </td>
                                    </tr>
                                 ))
                              ) : (
                                 <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center">
                                       <div className="flex flex-col items-center justify-center text-gray-500">
                                          <FaSearch className="w-12 h-12 mb-2 text-gray-300" />
                                          <p className="text-lg font-medium">No items found</p>
                                          <p className="text-sm mt-1">
                                             {searchQuery || filterInRepair
                                                ? 'Try adjusting your search or filter criteria'
                                                : 'No items available for selection'}
                                          </p>
                                       </div>
                                    </td>
                                 </tr>
                              )}
                           </tbody>
                        </table>
                     </div>
                  </div>
               </div>

               {/* Footer */}
               <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                     <div className="text-sm text-gray-600 flex items-center">
                        <FaInfoCircle className="w-4 h-4 mr-2 text-gray-400" />
                        {isSaving ? (
                           <span className="flex items-center text-blue-600 font-medium">
                              <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                              Saving selection, please wait...
                           </span>
                        ) : selectedCount > 0 ? (
                           <span>
                              <strong>{selectedCount}</strong> item{selectedCount !== 1 ? 's' : ''} ready for CRM
                              workflow
                           </span>
                        ) : (
                           <span>No items selected</span>
                        )}
                     </div>
                     <div className="flex space-x-3">
                        <button
                           onClick={onClose}
                           disabled={isSaving}
                           className={`px-6 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 flex items-center ${
                              isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                           }`}>
                           <FaTimes className="w-4 h-4 mr-2" />
                           Cancel
                        </button>
                        <button
                           onClick={handleSave}
                           disabled={isSaving || selectedCount === 0}
                           className={`px-6 py-2 border border-transparent rounded-lg text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 flex items-center justify-center min-w-[140px] ${
                              isSaving || selectedCount === 0
                                 ? 'bg-gray-400 cursor-not-allowed'
                                 : 'bg-blue-600 hover:bg-blue-700'
                           }`}>
                           {isSaving ? (
                              <>
                                 <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                 Saving...
                              </>
                           ) : (
                              <>
                                 <FaCheck className="w-4 h-4 mr-2" />
                                 Save Selection ({selectedCount})
                              </>
                           )}
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

export default SerialNumberModal;
